import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  Select,
  useToast,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaUsers,
  FaUser,
  FaPhone,
  FaIdCard,
  FaHome,
  FaToggleOn,
} from "react-icons/fa";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Create_Driver = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [staffType, setStaffType] = useState("driver");
  const [name, setName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [address, setAddress] = useState("");
  const [drivingLicenseNo, setDrivingLicenseNo] = useState("");
  const [status, setStatus] = useState(false);

  const toast = useToast();

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const resetForm = () => {
    setStaffType("driver");
    setName("");
    setPhoneNo("");
    setAddress("");
    setDrivingLicenseNo("");
    setStatus(false);
  };

  const handleStaffTypeChange = (e) => setStaffType(e.target.value);

  const handleSubmit = async () => {
    // Replace with actual values for user_id and school_id
    const userId = sessionStorage.getItem("user_id");
    const schoolId = sessionStorage.getItem("school_id");

    const payload = {
      user_id: userId,
      school_id: schoolId,
      name,
      phone_no: phoneNo,
      address,
      driving_license_no: staffType === "driver" ? drivingLicenseNo : "",
      staff_type: staffType,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/admin/bus/staff/create_bus_staff.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        resetForm(); // Reset the form after a successful submission
        closeModal();
      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create staff. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button
        size={{ base: "xs", md: "md" }}
        leftIcon={<FaPlus />}
        colorScheme="blue"
        onClick={openModal}
      >
        Staff
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        isCentered
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add New Staff
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl mb={4}>
                <FormLabel>
                  <Icon as={FaUsers} mr={1} color="teal.500" />
                  Staff Type
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  onChange={handleStaffTypeChange}
                  value={staffType}
                >
                  <option value="driver">Driver</option>
                  <option value="conductor">Conductor</option>
                </Select>
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>
                  <Icon as={FaUser} mr={1} color="teal.500" />
                  Name
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>
                  <Icon as={FaPhone} mr={1} color="teal.500" />
                  Phone Number
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter Phone Number"
                  value={phoneNo}
                  onChange={(e) => setPhoneNo(e.target.value)}
                />
              </FormControl>

              {staffType === "driver" && (
                <FormControl mb={4}>
                  <FormLabel>
                    <Icon as={FaIdCard} mr={1} color="teal.500" />
                    Driving License Number
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    placeholder="Enter Driving License Number"
                    value={drivingLicenseNo}
                    onChange={(e) => setDrivingLicenseNo(e.target.value)}
                  />
                </FormControl>
              )}

              <FormControl mb={4}>
                <FormLabel>
                  <Icon as={FaHome} mr={1} color="teal.500" />
                  Address
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Enter Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </FormControl>
            </SimpleGrid>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">
                {/* <Icon as={FaToggleOn} mr={1} color="teal.500" /> */}
                Status
              </FormLabel>
              <Switch
                id="staff-status"
                colorScheme="blue"
                isChecked={status}
                onChange={(e) => setStatus(e.target.checked)}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.500">
              Change the Status by toggle
            </Text>
          </ModalBody>

          <ModalFooter>
            {/* <Button variant="ghost" mr={3} onClick={closeModal}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Add Staff
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Create_Driver;
